/**
 * /learn — Financial literacy hub for teens & young adults.
 * 5 lessons using the same math engine as the main calculator but wrapped
 * in kid-friendly (not condescending) copy.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkles, Shield, Landmark, TrendingUp, Waves, ClipboardCheck, Wrench } from "lucide-react";
import useSEO from "@/lib/useSEO";

const LESSONS = [
  {
    slug: "compound",
    icon: Sparkles,
    title: "Compound interest — the trick nobody teaches",
    blurb: "$5/day at 15 beats $15/day at 35. See it for yourself.",
    time: "5 min",
  },
  {
    slug: "emergency-fund",
    icon: Shield,
    title: "Emergency fund — your first savings goal",
    blurb: "Why 3 months of expenses in cash comes before anything else.",
    time: "4 min",
  },
  {
    slug: "first-roth",
    icon: Landmark,
    title: "Your first Roth IRA",
    blurb: "The tax-free growth account that&apos;s basically free money.",
    time: "6 min",
  },
  {
    slug: "four-percent",
    icon: TrendingUp,
    title: "The 4% rule (and why it&apos;s wrong)",
    blurb: "The math that decides how much you can spend when you retire.",
    time: "7 min",
  },
  {
    slug: "sequence-risk",
    icon: Waves,
    title: "Sequence of returns — the silent killer",
    blurb: "Why WHEN the market drops matters more than IF.",
    time: "6 min",
  },
  {
    slug: "readiness-checklist",
    icon: ClipboardCheck,
    title: "Retirement readiness checklist — score /50",
    blurb: "9 sections, 50 questions, live score. Find the one seed to plant today.",
    time: "5 min",
  },
  {
    slug: "readiness-workbook",
    icon: Wrench,
    title: "Retirement readiness workbook — score /100",
    blurb: "14 parts, 10 toolboxes, guided budget + action plan + letter to Future You.",
    time: "30 min",
  },
];

const ARTICLES = [
  {
    slug: "one-percent-more-spending-study",
    title: "Study: what 1% more spending really costs",
    blurb: "45,000 simulations — the same $10k raise costs 2 points or 18.",
    hub: "Original Research",
  },
  {
    slug: "planning-to-100-study",
    title: "Study: retirement success from 90 to 100",
    blurb: "105,000 simulations reveal the cliff at 5% withdrawals.",
    hub: "Original Research",
  },
  {
    slug: "how-long-will-a-million-last",
    title: "How long will $1 million last?",
    blurb: "From 12 years to forever — real simulation data.",
    hub: "Calculators",
  },
  {
    slug: "retire-at-62-checklist",
    title: "Retiring at 62: the complete checklist",
    blurb: "Insurance gap, Social Security cut, 38-year horizon.",
    hub: "Readiness",
  },
  {
    slug: "retire-with-500k",
    title: "Can I retire with $500,000?",
    blurb: "The honest math — when it works, when it doesn't.",
    hub: "Calculators",
  },
  {
    slug: "social-security-claiming-age",
    title: "Social Security at 62 vs 67 vs 70",
    blurb: "The claiming decision that swings six figures.",
    hub: "Social Security",
  },
  {
    slug: "retirement-healthcare-costs",
    title: "Retirement healthcare costs",
    blurb: "What Medicare covers, what it doesn't, and the pre-65 gap.",
    hub: "Healthcare",
  },
  {
    slug: "how-much-to-retire-at-65",
    title: "How much do I need to retire at 65?",
    blurb: "A formula you can compute in five minutes.",
    hub: "Readiness",
  },
  {
    slug: "medication-list-aging-parents",
    title: "Medication list for aging parents",
    blurb: "The 7 fields every entry needs, plus free printables.",
    hub: "Family & Legacy",
  },
];

export default function LearnIndex() {
  useSEO({
    title: "Learn · Free financial literacy for young adults · Centennial",
    description: "Free interactive lessons on compound interest, emergency funds, Roth IRAs, and safe withdrawal rates — designed for teens and young adults who want to understand money before they need to.",
    canonicalPath: "/learn",
  });

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2B5339] mb-10"
        >
          <ArrowLeft size={14} /> Back to calculator
        </Link>

        <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-2">
          Free · No signup · For young adults
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C2B23] leading-tight">
          Money math they<br />
          <span className="text-[#2B5339]">forgot to teach</span> you.
        </h1>
        <p className="mt-6 text-lg text-[#5C6B62] leading-relaxed max-w-2xl">
          Five interactive lessons that teach you what &ldquo;compound interest&rdquo;, &ldquo;emergency fund&rdquo;, and &ldquo;Roth IRA&rdquo; actually mean — and why understanding them before you&apos;re 25 is worth more than any raise you&apos;ll ever get.
        </p>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
          {LESSONS.map((l, i) => {
            const Icon = l.icon;
            return (
              <Link
                key={l.slug}
                data-testid={`learn-card-${l.slug}`}
                to={`/learn/${l.slug}`}
                className="group bg-white border border-[#E5E2DC] rounded-2xl p-6 hover:border-[#2B5339] hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="inline-flex w-10 h-10 rounded-full bg-[#F0EFEA] items-center justify-center text-[#2B5339]">
                    <Icon size={17} />
                  </div>
                  <span className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#5C6B62]">
                    #{i + 1} · {l.time}
                  </span>
                </div>
                <h3 className="text-lg font-medium text-[#1C2B23] mb-2 group-hover:text-[#2B5339] transition-colors" dangerouslySetInnerHTML={{ __html: l.title }} />
                <p className="text-sm text-[#5C6B62] leading-relaxed" dangerouslySetInnerHTML={{ __html: l.blurb }} />
                <div className="mt-4 text-xs text-[#2B5339] inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Start lesson <ArrowRight size={11} />
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-16">
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-2">
            Guides · real questions, honest answers
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {ARTICLES.map((a) => (
              <Link
                key={a.slug}
                data-testid={`article-card-${a.slug}`}
                to={`/learn/${a.slug}`}
                className="group bg-white border border-[#E5E2DC] rounded-2xl p-5 hover:border-[#2B5339] hover:shadow-sm transition-all"
              >
                <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#5C6B62] mb-2">
                  {a.hub}
                </div>
                <h3 className="text-lg font-medium text-[#1C2B23] mb-1 group-hover:text-[#2B5339] transition-colors">
                  {a.title}
                </h3>
                <p className="text-sm text-[#5C6B62] leading-relaxed">{a.blurb}</p>
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-16 bg-[#E8F0EB] border border-[#2B5339] rounded-2xl p-6 max-w-2xl">
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-2">
            For teachers and parents
          </div>
          <p className="text-sm text-[#1C2B23] leading-relaxed">
            These lessons are free to use in classrooms, homeschool curricula, or anywhere else. No accounts, no ads (from the reader&apos;s perspective — this site is funded by advisor listings). If you&apos;d like a printable PDF version of any lesson, or want to sponsor the literacy program, email us.
          </p>
        </div>
      </div>
    </div>
  );
}
